import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { formatRut } from '../utils/formatters';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';

const DashboardPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [visits, setVisits] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    
    // --- LÓGICA DE PERMISOS UNIFICADA ---
    const userPermissions = user?.permissions || {};
    const isSuperUser = user?.role === 'superuser';
    const isAdmin = user?.role === 'admin' || isSuperUser;

    useEffect(() => {
        const fetchVisits = async () => {
            try {
                const q = query(collection(db, 'visits'), orderBy('createdAt', 'desc'));
                const visitSnapshot = await getDocs(q);
                setVisits(visitSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error("Error fetching visits: ", error);
                setNotification({ message: 'Error al cargar los informes.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchVisits();
    }, [setNotification]);

    const handleDeleteVisit = async (e, visitId, photos) => {
        e.stopPropagation();
        if (!window.confirm(`¿Seguro que quieres eliminar el informe ${visitId}?`)) return;
        try {
            if (photos && photos.length > 0) {
                await Promise.all(photos.map(photo => deleteObject(ref(storage, photo.url)).catch(err => console.warn("Error borrando fotos:", err))));
            }
            await deleteDoc(doc(db, "visits", visitId));
            setVisits(prev => prev.filter(visit => visit.id !== visitId));
            setNotification({ message: `Informe ${visitId} eliminado.`, type: 'success' });
        } catch (error) {
            setNotification({ message: 'Error al eliminar el informe.', type: 'error' });
        }
    };

    const filteredVisits = visits.filter(visit =>
        (visit.id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (visit.clientName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (visit.clientId?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    const formatDate = (timestamp) => {
        if (!timestamp?.seconds) return 'N/A';
        return new Date(timestamp.seconds * 1000).toLocaleDateString('es-CL');
    }

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
            <div className="flex items-center mb-4">
                 <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4">&larr; Volver</button>
                <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Gestión de Informes de Visita</h1>
            </div>
            <p className="mb-6 text-gray-600 dark:text-gray-400">Busca, revisa y gestiona todos los informes de visitas técnicas.</p>
            
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                {/* Botón Nuevo Informe: Visible para Admin y Superuser */}
                {(isAdmin || userPermissions.reports?.create) && (
                    <button onClick={() => setPage('form')} className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white px-5 py-2 rounded-lg transition">+ Nuevo Informe</button>
                )}
                <input type="text" placeholder="Buscar por Folio, RUT o Cliente..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="px-4 py-2 border dark:border-gray-700 rounded-lg w-full md:w-1/3 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"/>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-x-auto border border-transparent dark:border-gray-700 transition-colors duration-300">
                <table className="min-w-full">
                    <thead className="bg-gray-100 dark:bg-gray-700/50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Folio</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Fecha</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cliente</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">RUT</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Técnico</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {loading ? (
                            <tr><td colSpan="6" className="text-center py-10 dark:text-gray-400">Cargando...</td></tr>
                        ) : (
                            filteredVisits.map(visit => (
                                <tr key={visit.id} onClick={() => setPage('viewReport', visit.id)} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition">
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600 dark:text-blue-400">{visit.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400">{formatDate(visit.fecha)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-800 dark:text-gray-200 font-semibold">{visit.clientName}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400">{formatRut(visit.clientId)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-600 dark:text-gray-400">{visit.tecnico}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="flex items-center justify-center gap-3">
                                            {/* Botón Editar */}
                                            {(isAdmin || userPermissions.reports?.edit) && (
                                                <button 
                                                    onClick={(e) => { e.stopPropagation(); setPage('editForm', visit.id); }} 
                                                    className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300" 
                                                    title="Editar"
                                                >
                                                    <PencilSquareIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                            {/* Botón Eliminar */}
                                            {(isAdmin || userPermissions.reports?.delete) && (
                                                <button 
                                                    onClick={(e) => handleDeleteVisit(e, visit.id, visit.fotos)} 
                                                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300" 
                                                    title="Eliminar"
                                                >
                                                    <TrashIcon className="w-5 h-5"/>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                 { !loading && filteredVisits.length === 0 && <p className="text-center py-10 text-gray-500 dark:text-gray-400">No se encontraron informes.</p>}
            </div>
        </div>
    );
};

export default DashboardPage;